import { CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Skeleton } from '@/components/ui/skeleton';
import type { Hotspot } from './hotspots';
import { ProgressBar } from './ProgressBar';

/**
 * «Da sistemare per primo» (U8, lead with what waits): the folders with the most findings, each
 * one a link that opens it. The tree beside it stays as the way to move around.
 */
export function WorkList({
  rows,
  loading,
  linkFor,
}: {
  readonly rows: readonly Hotspot[];
  readonly loading: boolean;
  readonly linkFor: (id: string | undefined) => string;
}) {
  const { t } = useTranslation('archive');
  let body;
  if (loading) {
    body = <Skeleton className="h-32 w-full" />;
  } else if (rows.length === 0) {
    body = (
      <p className="flex items-center gap-2 text-sm">
        <CircleCheck aria-hidden="true" className="size-4" />
        {t('overview.workNone')}
      </p>
    );
  } else {
    body = (
      <ol className="flex list-none flex-col p-0">
        {rows.map(({ folder, drive, toFix }) => {
          const progress = folder.progress ?? { total: 0, inLine: 0 };
          return (
            <li
              key={folder.id}
              className="border-border flex flex-col gap-1.5 border-t py-3 first:border-t-0"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="min-w-0">
                  <Link
                    to={linkFor(folder.id)}
                    className="inline-flex min-h-11 items-center font-semibold break-words underline"
                  >
                    {folder.name}
                  </Link>{' '}
                  <span className="text-muted-foreground text-sm">{drive.name}</span>
                </span>
                <span className="text-sm font-semibold">
                  {t('overview.toFixCount', { count: toFix })}
                </span>
              </div>
              <ProgressBar {...progress} label={folder.name} />
              <p className="text-muted-foreground text-sm">{t('overview.workInLine', progress)}</p>
            </li>
          );
        })}
      </ol>
    );
  }
  return (
    <section
      aria-labelledby="archive-work-title"
      className="bg-card border-border rounded-md border p-4"
    >
      <h3 id="archive-work-title" className="pb-1 text-lg font-bold">
        {t('overview.workTitle')}
      </h3>
      {body}
    </section>
  );
}
